import React from "react";
import { CircleAlert, CircleCheckBig, Info } from "lucide-react";
import ContentsContainer from "../../components/ContentsContainer";

const sections = [
{
    sectionId: "purpose",
    sectionTitle: "1. Purpose",
    searchKeywords: `
      matrix referral reward levels role classifications
      payment structure special campaign arrangements
      referral program policy review update
      hiring priorities market conditions candidate availability
      client requirements recruitment difficulty approved budgets
      vacancies formally announced referral eligible
    `,
    sectionBody: (
      <>
        <div className="space-y-4 text-sm leading-relaxed">
          <p>
            This matrix lays out our standard referral reward levels, role classifications, payment structure, and special
            campaign arrangements under the Employee Referral Program Policy          
          </p>

          <p>
            We may review and update this matrix separately from the main policy to reflect:
          </p>

          <ul className="list-none space-y-1.5 pl-2">
            {[
              "Hiring priorities",
              "Market conditions",
              "Candidate availability",
              "Client requirements",
              "Recruitment difficulty",
              "Approved budgets",
              "Only vacancies formally announced as referral-eligible qualify for a reward.",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </>
    ),
  },

  {
    sectionId: "standard",
    sectionTitle: "2. Standard Referral Reward Levels",
    searchKeywords: `
      referral range general classification gross reward
      installment successful commencement experienced professional
      skilled support specialist highly technical critical
      niche difficult-to-fill administrative high-volume roles
      entry-level support Hyvid Points three months
    `,
    sectionBody: (
      <div className="space-y-4 text-sm leading-relaxed">
        <div>          
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ml-3">
            <table className="w-full text-xs table-fixed">
              <thead>
                <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                  <th className="px-3 py-2 text-left font-semibold w-[10%]">Referral Range</th>
                  <th className="px-3 py-2 text-left font-semibold w-[23%]">General Classification </th>
                  <th className="px-3 py-2 text-left font-semibold w-[17%]">Total Gross Reward</th>
                  <th className="px-3 py-2 text-left font-semibold w-[29%]">First Installment (30%)</th>
                  <th className="px-3 py-2 text-left font-semibold w-[21%]">Second Installment (70%)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-gray-100 dark:border-gray-800 dark:bg-gray-900/10">
                  <td className="px-3 py-2 align-top font-semibold text-purple-500 dark:text-purple-400">Range A</td>
                  <td className="px-3 py-2 align-top">Entry-level, administrative, general support, or high-volume roles </td>
                  <td className="px-3 py-2 align-top font-semibold"><p>PHP 4,000 </p><span className="text-emerald-500">+ 1,000 Hyvid Points</span> </td>
                  <td className="px-3 py-2 align-top"><p>PHP 1,200 after successful commencement </p><span className="text-emerald-500">+ 500 Hyvid Points</span></td>
                  <td className="px-3 py-2 align-top"><p>PHP 2,800 after three months </p><span className="text-emerald-500">+ 500 Hyvid Points</span></td>
                </tr>
                <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                  <td className="px-3 py-2 align-top font-semibold text-purple-500 dark:text-purple-400">Range B</td>
                  <td className="px-3 py-2 align-top">Experienced professional, skilled support, or specialist roles</td>
                  <td className="px-3 py-2 align-top font-semibold"><p>PHP 9,000</p><span className="text-emerald-500">+ 1,000 Hyvid Points</span> </td>
                  <td className="px-3 py-2 align-top"><p>PHP 2,700 after successful commencement</p><span className="text-emerald-500">+ 500 Hyvid Points</span></td>
                  <td className="px-3 py-2 align-top"><p>PHP 6,300 after three months</p><span className="text-emerald-500">+ 500 Hyvid Points</span></td>
                </tr>
                <tr className="border-t border-gray-100 dark:border-gray-800 dark:bg-gray-900/10">
                  <td className="px-3 py-2 align-top font-semibold text-purple-500 dark:text-purple-400">Range C</td>
                  <td className="px-3 py-2 align-top">Highly technical, critical, niche, or difficult-to-fill roles</td>
                  <td className="px-3 py-2 align-top font-semibold"><p>PHP 14,000</p><span className="text-emerald-500">+ 1,000 Hyvid Points</span> </td>
                  <td className="px-3 py-2 align-top"><p>PHP 4,200 after successful commencement</p><span className="text-emerald-500">+ 500 Hyvid Points</span></td>
                  <td className="px-3 py-2 align-top"><p>PHP 9,800 after three months</p><span className="text-emerald-500">+ 500 Hyvid Points</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-xs p-2 text-xs text-gray-500">All amounts are gross and subject to applicable taxes, statutory deductions, and payroll withholding.</p>
        </div>
      </div>
    ),
  },
  {
  sectionId: "role",
  sectionTitle: "3. Role Classification Guide",
  searchKeywords: `
    referral range specific vacancy recruitment difficulty
    profession title hiring need entry-level junior
    administrative customer operational data processing
    back-office high-volume candidate availability broad
    readily limited specialist experience intermediate senior
    professional skilled industry finance legal
    healthcare marketing technical support independence
    leadership incentives competitive client-specific systems
    process knowledge managerial niche expertise critical
    business positions substantial credentials specialised
    market
  `,
  sectionBody: (
    <>
      <div className="space-y-4 text-sm  leading-relaxed">
        <p>We assign the referral range to each specific vacancy based on its requirements and recruitment difficulty
          — a profession or job title won't automatically stay in the same range for every hiring need.
        </p>

        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          Range A
        </h3>

        <p>Range A may apply to:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Entry-level positions.",
            "Junior administrative roles.",
            "General customer or operational support.",
            "Data processing and general back-office roles.",
            "High-volume hiring requirements.",
            "Vacancies with a broad and readily available candidate market.",
            "Roles requiring limited prior specialist experience.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

                <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          Range B
        </h3>

        <p>Range B may apply to:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Intermediate to Senior professional roles.",
            "Skilled administrative or operational support.",
            "Roles requiring relevant industry experience.",
            "Specialist customer, finance, legal, healthcare, marketing, or technical support.",
            "Positions requiring moderate independence.",
            "Leadership positions approved for referral incentives.",
            "Roles with a smaller or more competitive candidate market.",
            "Vacancies requiring client-specific systems or process knowledge.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <h3 className="text-sm font-bold title-text mb-1.5 flex items-center gap-2">
          Range C
        </h3>

        <p>Range C may apply to:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Highly technical roles.",
            "Managerial roles.",
            "Roles requiring niche industry expertise.",
            "Critical client or business positions.",
            "Vacancies with limited candidate availability.",
            "Roles that have remained difficult to fill.",
            "Positions requiring substantial independence, professional credentials, or specialised market knowledge",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </>
  ),
},
{
  sectionId: "factors",
  sectionTitle: "4. Classification Factors",
  searchKeywords: `
    referral range Talent Acquisition assigning
    seniority responsibility independence decision-making
    technical complexity professional industry-specific knowledge
    candidate scarcity qualified availability target market
    client requirements systems qualifications compliance
    recruitment urgency start date vacancy impact
    business criticality operational revenue compliance
    market competition talent competing employers
    hiring history sourcing difficulty application volume
    offer acceptance rates final assignment approved
    referral-eligible vacancy announced
  `,
  sectionBody: (
    <>
      <div className="space-y-4 text-sm  leading-relaxed">
          <div>          
            <p>When assigning a referral range, Talent Acquisition considers:</p>
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ml-3">
              <table className="w-full text-xs w-fixed">
                <thead>
                  <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                    <th className="px-3 py-2 text-left font-semibold">Factor</th>
                    <th className="px-3 py-2 text-left font-semibold">Consideration</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100 dark:border-gray-800 dark:bg-gray-900/10">
                    <td className="px-3 py-2 align-top">Seniority</td>
                    <td className="px-3 py-2 align-top">Level of responsibility, independence, and decision-making required</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                    <td className="px-3 py-2 align-top">Technical complexity</td>
                    <td className="px-3 py-2 align-top">Depth of technical, professional, or industry-specific knowledge</td>
                  </tr>
                  <tr className="border-t border-gray-100 dark:border-gray-800 dark:bg-gray-900/10">
                    <td className="px-3 py-2 align-top">Candidate scarcity</td>
                    <td className="px-3 py-2 align-top">Availability of qualified candidates in the target market</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                    <td className="px-3 py-2 align-top">Client requirements </td>
                    <td className="px-3 py-2 align-top">Specific systems, qualifications, experience, or compliance requirements</td>
                  </tr>
                  <tr className="border-t border-gray-100 dark:border-gray-800 dark:bg-gray-900/10">
                    <td className="px-3 py-2 align-top">Recruitment urgency</td>
                    <td className="px-3 py-2 align-top">Required start date and impact of an unfilled vacancy</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                    <td className="px-3 py-2 align-top">Business criticality</td>
                    <td className="px-3 py-2 align-top">Operational, revenue, compliance, or client impact</td>
                  </tr>
                  <tr className="border-t border-gray-100 dark:border-gray-800 dark:bg-gray-900/10">
                    <td className="px-3 py-2 align-top">Market competition</td>
                    <td className="px-3 py-2 align-top">Demand for the same talent among competing employers</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                    <td className="px-3 py-2 align-top">Hiring history</td>
                    <td className="px-3 py-2 align-top">Previous sourcing difficulty, application volume, or offer acceptance rates</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs p-2 text-xs text-gray-500"><strong>The final range assignment is approved before the vacancy is announced as referral-eligible.</strong></p>
          </div>
      </div>
    </>
  ),
},
{
  sectionId: "assignment",
  sectionTitle: "5. Initial Role Assignment Guide",
  searchKeywords: `
    examples initial guide final classification vacancy-specific
    junior administrative data support customer service
    back-office recruitment operations entry-level
    experienced executive assistant coordinator
    accountant payroll specialist finance processor
    paralegal conveyancing support digital marketing
    design reporting intermediate software systems
    senior accountant senior paralegal specialist
    supervisor team leader software developer engineer
    technical lead manager specialised legal healthcare
    compliance critical persistently difficult-to-fill
    special campaign
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>These examples are just an initial guide — final classification stays vacancy-specific.</p>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm ml-3">
          <table className="w-full text-xs w-fixed">
            <thead>
              <tr className="bg-gradient-to-r from-emerald-600 to-purple-600 text-white">
                <th className="px-3 py-2 text-left font-semibold">Example Role Type </th>
                <th className="px-3 py-2 text-left font-semibold">Indicative Range</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-gray-100 dark-mode-card">
                <td className="px-3 py-2 align-top">Junior administrative or data support</td>
                <td className="px-3 py-2 align-top">Range A</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                <td className="px-3 py-2 align-top">General customer service or back-office support </td>
                <td className="px-3 py-2 align-top">Range A</td>
              </tr>
              <tr className="border-t border-gray-100 dark-mode-card">
                <td className="px-3 py-2 align-top">Entry-level recruitment or operations support </td>
                <td className="px-3 py-2 align-top">Range A</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                <td className="px-3 py-2 align-top">Experienced executive assistant or coordinator</td>
                <td className="px-3 py-2 align-top">Range B</td>
              </tr>
              <tr className="border-t border-gray-100 dark-mode-card">
                <td className="px-3 py-2 align-top">Accountant, payroll specialist, or finance processor</td>
                <td className="px-3 py-2 align-top">Range B</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                <td className="px-3 py-2 align-top">Paralegal or conveyancing support role </td>
                <td className="px-3 py-2 align-top">Range B</td>
              </tr>
              <tr className="border-t border-gray-100 dark-mode-card">
                <td className="px-3 py-2 align-top">Digital marketing, design, or reporting specialist</td>
                <td className="px-3 py-2 align-top">Range B</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                <td className="px-3 py-2 align-top">Intermediate software, systems, or data role </td>
                <td className="px-3 py-2 align-top">Range B</td>
              </tr>
              <tr className="border-t border-gray-100 dark-mode-card">
                <td className="px-3 py-2 align-top">Senior accountant, senior paralegal, or senior specialist </td>
                <td className="px-3 py-2 align-top">Range B</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                <td className="px-3 py-2 align-top">Supervisor or team leader </td>
                <td className="px-3 py-2 align-top">Range B</td>
              </tr>
              <tr className="border-t border-gray-100 dark-mode-card">
                <td className="px-3 py-2 align-top">Senior software developer, engineer, or technical lead </td>
                <td className="px-3 py-2 align-top">Range C</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                <td className="px-3 py-2 align-top">Manager</td>
                <td className="px-3 py-2 align-top">Range C</td>
              </tr>
              <tr className="border-t border-gray-100 dark-mode-card">
                <td className="px-3 py-2 align-top">Highly specialised legal, finance, healthcare, or compliance role</td>
                <td className="px-3 py-2 align-top">Range C</td>
              </tr>
              <tr className="border-t border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900/30">
                <td className="px-3 py-2 align-top">Critical or persistently difficult-to-fill vacancy </td>
                <td className="px-3 py-2 align-top">Range C or Special Campaign</td>
              </tr>
            </tbody>
          </table>
        </div>
    </div>
  ),
},

{
  sectionId: "campaigns",
  sectionTitle: "6. Special Referral Campaigns",
  searchKeywords: `
    temporary enhanced referral incentives selected
    client launches urgent vacancies high-volume hiring
    campaigns critical positions difficult-to-fill roles
    business requirements location-specific recruitment needs
    special campaign rewards standard period
    addition expressly approved payment schedule
    specified teams locations clients vacancies
    non-cash rewards recognition announcement replaces
    supplements
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>From time to time, Talent Acquisition may recommend temporary or enhanced referral incentives for selected:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Client launches",
            "Urgent vacancies",
            "High-volume hiring campaigns",
            "Critical positions",
            "Difficult-to-fill roles",
            "New business requirements",
            "Location-specific recruitment needs",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>

        <p>Special campaign rewards may:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Replace the standard reward for the campaign period",
            "Be paid in addition to the standard reward where expressly approved",
            "Use a different payment schedule",
            "Apply only to specified teams, locations, clients, or vacancies",
            "Include non-cash rewards or recognition",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        <p>Every campaign announcement will clearly state whether the special reward replaces or supplements the standard reward.</p>
    </div>
  ),
},

{
  sectionId: "terms",
  sectionTitle: "7. Standard Payment Terms",
  searchKeywords: `
    approved campaign announcement otherwise
    thirty percent payable referred candidate
    successfully starts completes onboarding
    remaining seventy percent calendar months
    continuous service actively employed
    applicable milestone validly earned installment
    payable separate payroll validation
    practicable payroll gross amounts deductions
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
       <p>Unless an approved campaign announcement states otherwise:</p>
        <ul className="list-none space-y-1.5 pl-2">
          {[
            "Thirty percent (30%) is payable once your referred candidate successfully starts and completes the required onboarding.",
            "The remaining seventy percent (70%) is payable after your referred candidate completes three (3) calendar months of continuous service.",
            "Both you and your referred candidate must be actively employed on the applicable milestone date.",
            "Once an installment has been validly earned, it stays payable even if you separate before the payroll date, subject to the Employee Referral Program Policy.",
            "We'll process payment through the next practicable payroll after validation.",
            "All amounts are gross and subject to applicable deductions.",
          ].map((item) => (
            <li key={item} className="flex items-start gap-2">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
    </div>
  ),
},
{
  sectionId: "requirements",
  sectionTitle: "8. Approval Requirements",
  searchKeywords: `
    changes matrix approval People Culture
    Talent Acquisition Finance budget confirmation
    Co-Founders Managing Director COO
    standard reward amounts vacancy assignment
    referral range special campaign introduction
    rewards above Range C standard
    non-cash supplementary incentives
  `,
  sectionBody: (
    <div className="space-y-4 text-sm  leading-relaxed">
      <p>Changes to this matrix require approval from:</p>
      <ul className="list-none space-y-1.5 pl-2">
        {[
          "People & Culture or Talent Acquisition.",
          "Finance, where budget confirmation is required.",
          "Co-Founders, Managing Director, COO",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>

      <p>Approval is required for:</p>
      <ul className="list-none space-y-1.5 pl-2">
        {[
          "Changes to standard reward amounts",
          "Assignment of a vacancy to a referral range",
          "Introduction of a special campaign",
          "Rewards above the standard Range C amount",
          "Rewards above the standard Range C amount",
          "Non-cash or supplementary incentives",
        ].map((item) => (
          <li key={item} className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  ),
},

];

const GrievanceDisputeContents = ({ searchTerm }) => {
  const filteredSections = sections.filter((section) => {
    if (!searchTerm) return true;
    
    const search = searchTerm.toLowerCase();
    
    return (
      section.sectionTitle.toLowerCase().includes(search) ||
      section.searchKeywords?.toLowerCase().includes(search)
    );
  });
  return (
    <ContentsContainer sections={filteredSections} />
  );
};

export default GrievanceDisputeContents;